import React, { useState } from 'react';
import { X, Gift, Building2, Calendar, DollarSign, FileText, Image, ExternalLink, Edit2, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { deleteRebate } from '../../api/wholesale/rebates';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const RebateDetailModal = ({ isOpen, onClose, rebate, onEdit, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !rebate) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPeriod = () => {
    if (rebate.rebate_type === 'monthly') {
      return `${MONTH_NAMES[rebate.rebate_month - 1]} ${rebate.rebate_year}`;
    }
    return `Year ${rebate.rebate_year}`;
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${rebate.rebate_type} rebate for ${formatPeriod()}?`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteRebate(rebate.id);
      toast.success('Rebate deleted');
      onDeleted?.();
      onClose();
    } catch (error) {
      console.error('Error deleting rebate:', error);
      toast.error(error.response?.data?.message || 'Failed to delete rebate');
    } finally {
      setDeleting(false);
    }
  };

  const isImage = rebate.receipt_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPdf = rebate.receipt_url?.match(/\.pdf$/i);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Rebate Details</h2>
              <p className="text-sm text-gray-500">{formatPeriod()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onEdit && (
              <button
                onClick={() => onEdit(rebate)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                title="Edit"
                disabled={deleting}
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
              title="Delete"
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={deleting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Amount - Prominent Display */}
          <div className="text-center bg-emerald-50 rounded-xl p-6">
            <p className="text-sm text-emerald-600 font-medium mb-1">Rebate Amount</p>
            <p className="text-3xl font-bold text-emerald-700">{formatCurrency(rebate.amount)}</p>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Company */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Company</span>
              </div>
              <p className="font-medium text-gray-900">{rebate.company_name}</p>
            </div>

            {/* Type */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Gift className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Type</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                rebate.rebate_type === 'monthly'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {rebate.rebate_type === 'monthly' ? 'Monthly' : 'Yearly'}
              </span>
            </div>

            {/* Period */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Period</span>
              </div>
              <p className="font-medium text-gray-900">{formatPeriod()}</p>
            </div>

            {/* Received Date */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Received</span>
              </div>
              <p className="font-medium text-gray-900">
                {rebate.received_date 
                  ? new Date(rebate.received_date).toLocaleDateString('en-NG', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })
                  : 'Not specified'
                }
              </p>
            </div>
          </div>

          {/* Description */}
          {rebate.description && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <FileText className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Description</span>
              </div>
              <p className="text-gray-700">{rebate.description}</p>
            </div>
          )}

          {/* Receipt */}
          {rebate.receipt_url && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2 text-gray-600">
                  <Image className="w-4 h-4" />
                  <span className="text-sm font-medium">Receipt Attached</span>
                </div>
                <a
                  href={rebate.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </a>
              </div>
              
              {isImage && (
                <div className="p-4 bg-white flex justify-center">
                  <img 
                    src={rebate.receipt_url} 
                    alt="Receipt" 
                    className="max-h-64 rounded-lg shadow-sm border"
                  />
                </div>
              )}
              
              {isPdf && (
                <div className="p-4 bg-white text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-lg">
                    <FileText className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-medium">PDF Document</p>
                      <p className="text-sm">Click "Open" to view</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-gray-400 pt-4 border-t border-gray-100">
            <p>Created: {new Date(rebate.created_at).toLocaleString('en-NG')}</p>
            {rebate.updated_at !== rebate.created_at && (
              <p>Updated: {new Date(rebate.updated_at).toLocaleString('en-NG')}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RebateDetailModal;

